import React, { useContext } from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { useService } from '@pushly/aqe/lib/hooks/use-service'
import { AppService } from '../../../../services'
import {
    isWebPromptGroupDisplayConditions,
    WebPromptGroupDisplayConditions,
} from '@pushly/models/lib/structs/prompts/prompt-group-display-conditions'
import { PromptGroupDisplayCondition } from '@pushly/models/lib/enums/prompts/prompt-group-display-condition'
import { PromptGroupConditions } from '@pushly/models/lib/structs/prompts/prompt-group-conditions'
import { PromptEditorDispatchContext } from '../reducer'
import { PromptEditorContext } from '../context'
import { DEFAULT_DESKTOP_DISPLAY_CONDITION, DEFAULT_MOBILE_DISPLAY_CONDITION } from '../constants'
import { Divider, Form, Popover, Switch } from 'antd'

type Props = {
    onSubmit: () => void
    onCancel: () => void
}

export const PromptGroupOptionsEditor: React.FC<Props> = ({ onSubmit, onCancel }) => {
    const dispatch = useContext(PromptEditorDispatchContext)
    const context = useContext(PromptEditorContext)
    const { loading, promptGroup, isAutoShowEnabled } = context

    const appSvc = useService(AppService)

    const groupConditions = promptGroup?.conditions ?? new PromptGroupConditions()
    const groupDisplayConditions = isWebPromptGroupDisplayConditions(groupConditions.display)
        ? groupConditions.display
        : WebPromptGroupDisplayConditions.from({
              desktop: DEFAULT_DESKTOP_DISPLAY_CONDITION,
              mobile: DEFAULT_MOBILE_DISPLAY_CONDITION,
          })

    const desktopDisplayCondition = groupDisplayConditions.desktop ?? DEFAULT_DESKTOP_DISPLAY_CONDITION
    const isDesktopEnabled = desktopDisplayCondition === PromptGroupDisplayCondition.ENABLED
    const isDesktopBypassEnabled = desktopDisplayCondition === PromptGroupDisplayCondition.PASS

    const mobileDisplayCondition = groupDisplayConditions.mobile ?? DEFAULT_MOBILE_DISPLAY_CONDITION
    const isMobileEnabled = mobileDisplayCondition === PromptGroupDisplayCondition.ENABLED
    const isMobileBypassEnabled = mobileDisplayCondition === PromptGroupDisplayCondition.PASS

    function handleGroupDisplayConditionsChange(conditions: WebPromptGroupDisplayConditions) {
        if (loading) return
        const groupUpdate = promptGroup.clone()
        groupUpdate.conditions = groupConditions.clone()
        groupUpdate.conditions.display = WebPromptGroupDisplayConditions.from({
            desktop: conditions.desktop ?? desktopDisplayCondition,
            mobile: conditions.mobile ?? mobileDisplayCondition,
        })
        dispatch({ type: 'set-prompt-group', group: groupUpdate })
    }

    return (
        <Well
            className="prompt-group-options-well"
            title="Additional Options"
            submitText="Save Prompt"
            onSubmit={onSubmit}
            onCancel={onCancel}
            footerMode="ghost"
        >
            <Form.Item layout="vertical">
                <label>
                    <Switch
                        size="small"
                        checked={isAutoShowEnabled}
                        onChange={(enabled) => {
                            dispatch({ type: 'set-is-auto-show-enabled', enabled })
                        }}
                        aria-label="Automatically prompt users"
                    />
                    <span className="label-with-component-l">
                        Automatically prompt users
                        <Popover
                            getPopupContainer={appSvc.getAppContainer}
                            classNames={{ content: 'prompt-info-popover' }}
                            content={
                                <div>
                                    By default this prompt will automatically show to your users if all conditions are
                                    met. You may choose to disable this behavior and trigger the prompt manually after
                                    the user has performed a specific action on your website via the Platform SDK.
                                    Contact your account manager for technical implementation information.
                                </div>
                            }
                            trigger="hover"
                        >
                            <ExclamationCircleOutlined className="info-icon" />
                        </Popover>
                    </span>
                </label>
            </Form.Item>

            <Divider />

            <Form.Item layout="vertical">
                <label>
                    <Switch
                        size="small"
                        checked={isDesktopEnabled}
                        onChange={(enabled) => {
                            const conditionsUpdate = groupDisplayConditions.clone()
                            conditionsUpdate.desktop = enabled
                                ? PromptGroupDisplayCondition.ENABLED
                                : PromptGroupDisplayCondition.PASS
                            handleGroupDisplayConditionsChange(conditionsUpdate)
                        }}
                        aria-label="Show prompt to desktop users"
                    />
                    <span className="label-with-component-l">Show prompt to desktop users</span>
                </label>
            </Form.Item>

            {!isDesktopEnabled && (
                <Form.Item layout="vertical" style={{ paddingLeft: 42 }}>
                    <label>
                        <Switch
                            size="small"
                            checked={isDesktopBypassEnabled}
                            onChange={(enabled) => {
                                const conditionsUpdate = groupDisplayConditions.clone()
                                conditionsUpdate.desktop = enabled
                                    ? PromptGroupDisplayCondition.PASS
                                    : PromptGroupDisplayCondition.DISABLED
                                handleGroupDisplayConditionsChange(conditionsUpdate)
                            }}
                            aria-label="Continue to next priority prompt for desktop"
                        />
                        <span className="label-with-component-l">Continue to next priority prompt</span>
                    </label>
                </Form.Item>
            )}

            <Divider />

            <Form.Item layout="vertical">
                <label>
                    <Switch
                        size="small"
                        checked={isMobileEnabled}
                        onChange={(enabled) => {
                            const conditionsUpdate = groupDisplayConditions.clone()
                            conditionsUpdate.mobile = enabled
                                ? PromptGroupDisplayCondition.ENABLED
                                : PromptGroupDisplayCondition.PASS
                            handleGroupDisplayConditionsChange(conditionsUpdate)
                        }}
                        aria-label="Show prompt to mobile users"
                    />
                    <span className="label-with-component-l">Show prompt to mobile users</span>
                </label>
                <Popover content="Mobile placement also includes tablet devices" trigger="hover">
                    <InfoCircleOutlined className="info-icon" />
                </Popover>
            </Form.Item>

            {!isMobileEnabled && (
                <Form.Item layout="vertical" style={{ paddingLeft: 42 }}>
                    <label>
                        <Switch
                            size="small"
                            checked={isMobileBypassEnabled}
                            onChange={(enabled) => {
                                const conditionsUpdate = groupDisplayConditions.clone()
                                conditionsUpdate.mobile = enabled
                                    ? PromptGroupDisplayCondition.PASS
                                    : PromptGroupDisplayCondition.DISABLED
                                handleGroupDisplayConditionsChange(conditionsUpdate)
                            }}
                            aria-label="Continue to next priority prompt for mobile"
                        />
                        <span className="label-with-component-l">Continue to next priority prompt</span>
                    </label>
                </Form.Item>
            )}
        </Well>
    )
}
